import { flow } from '@helo/utils'
import { AxisBottom, AxisLeft } from '@visx/axis'

import type { QueryOutput } from '../../Query'
import { RenderPipeline } from '../../RenderPipeline'
import type { ChartCartesianOutput } from '../ChartCartesian'
import type { ChartDataOutput } from '../ChartData'
import { useChartWrapperContext } from '../ChartWrapper/ChartWrapper'
import { ChartAxisConstants, ChartAxisStyles } from './ChartAxis.constants'
import {
  type AxisTickLabelOrientation,
  type ChartAxisOptions,
  type ChartAxisResolvedOptions,
  chartAxisOptions,
} from './ChartAxis.options'
import {
  ChartAxisInputParts,
  ChartAxisOutputParts,
  ChartAxisUtils,
} from './ChartAxis.parts'

export { chartAxisOptions } from './ChartAxis.options'
export type { ChartAxisOptions } from './ChartAxis.options'

/** Pipeline input consumed by ChartAxis.setup. */
export type ChartAxisInput = ChartAxisOptions

export type ChartResolvedAxis = ChartAxisResolvedOptions & {
  showTicks: boolean
  showLine: boolean
  ticks?: number[]
}

export type ChartAxisSlice = {
  x: ChartResolvedAxis
  y: ChartResolvedAxis
  xAxisIsDate: boolean
  xTickLineCount: number
  xTickLabelOrientation: AxisTickLabelOrientation
  yTickLabelOrientation: AxisTickLabelOrientation
  xTickLabels: string[]
  yTickLabels: string[]
}

/** Pipeline output slice — namespaced axis config. */
export type ChartAxisOutput = {
  axis: ChartAxisSlice
}

export interface ChartXAxisRenderProps
  extends Pick<ChartAxisSlice, 'x' | 'xAxisIsDate'> {}

export interface ChartYAxisRenderProps
  extends Pick<ChartAxisSlice, 'y' | 'xAxisIsDate'> {}

const chartAxisSetup = () =>
  RenderPipeline.createFeatureSetup<
    ChartAxisInput,
    ChartAxisOutput,
    ChartDataOutput & ChartCartesianOutput & Pick<QueryOutput, 'contentNode'>
  >(
    flow(
      ChartAxisInputParts.setupInput(),
      ChartAxisInputParts.setup_xAxisInput(),
      ChartAxisInputParts.setup_yAxisInput(),
      ChartAxisOutputParts.setAxis(),
    ),
  )

export interface ChartXAxisProps {
  renderConfig: ChartAxisOutput
}

export interface ChartYAxisProps {
  renderConfig: ChartAxisOutput
}

interface TickLabelProps {
  x: number
  y: number
  lines: string[]
  orientation: AxisTickLabelOrientation
  fill: string
  textAnchor?: 'start' | 'middle' | 'end'
  dominantBaseline?: 'middle' | 'auto'
  dx?: number
  dy?: number
}

const TickLabel = ({
  x,
  y,
  lines,
  orientation,
  fill,
  textAnchor = 'middle',
  dominantBaseline,
  dx = 0,
  dy = 0,
}: TickLabelProps) => {
  const rotate =
    orientation === 'diagonal' ? -45 : orientation === 'vertical' ? -90 : 0
  const anchor = orientation === 'horizontal' ? textAnchor : 'end'

  return (
    <text
      x={x}
      y={y}
      dx={dx}
      fill={fill}
      fontFamily={ChartAxisStyles.tickLabel.fontFamily}
      fontSize={ChartAxisStyles.tickLabel.fontSize}
      fontWeight={ChartAxisStyles.tickLabel.fontWeight}
      textAnchor={anchor}
      dominantBaseline={
        orientation === 'horizontal' ? dominantBaseline : undefined
      }
      transform={rotate === 0 ? undefined : `rotate(${rotate}, ${x}, ${y})`}
    >
      {lines.map((line, index) => (
        <tspan
          key={`${line}-${index}`}
          x={x}
          dy={index === 0 ? dy : ChartAxisStyles.tickLabel.fontSize}
        >
          {line}
        </tspan>
      ))}
    </text>
  )
}

const XAxis = ({ renderConfig }: ChartXAxisProps) => {
  const { innerHeight, innerWidth, scales } = useChartWrapperContext()
  const { x, xAxisIsDate } = renderConfig.axis
  const axisStroke = ChartAxisUtils.useAxisStroke()
  const tickFill = ChartAxisUtils.useTickLabelFill()
  const axisLabelFill = ChartAxisUtils.useAxisLabelFill()

  if (scales == null) return null

  const isHorizontal = scales.orientation === 'horizontal'
  const tickLength = x.showTicks ? ChartAxisConstants.axisTickLength : 0
  const tickKind = isHorizontal
    ? ('number' as const)
    : xAxisIsDate
      ? ('date' as const)
      : ('text' as const)
  const tickOptions = {
    kind: tickKind,
    formatState: x,
    layout: x.axisTickLabelLayout,
    truncation: x.axisTickLabelTruncation,
  }

  return (
    <>
      {x.showLine ? (
        <line
          x1={0}
          x2={innerWidth}
          y1={innerHeight}
          y2={innerHeight}
          stroke={axisStroke}
          strokeWidth={ChartAxisStyles.axisStrokeWidth}
          pointerEvents="none"
        />
      ) : null}
      <AxisBottom
        top={innerHeight}
        scale={scales.xScale}
        stroke="transparent"
        strokeWidth={ChartAxisStyles.axisStrokeWidth}
        strokeDasharray=""
        tickLength={tickLength}
        tickStroke={x.showTicks ? axisStroke : 'transparent'}
        tickValues={
          isHorizontal ? (x.ticks ?? renderConfig.axis.y.ticks) : undefined
        }
        tickFormat={value =>
          ChartAxisUtils.resolveTickLabelLines({
            ...tickOptions,
            raw: String(value),
          }).join('\n')
        }
        tickComponent={({ x: tickX, y, formattedValue }) => {
          const lines = String(formattedValue ?? '')
            .split('\n')
            .filter(Boolean)
          return (
            <TickLabel
              x={tickX}
              y={y}
              lines={lines}
              orientation={renderConfig.axis.xTickLabelOrientation}
              fill={tickFill}
              textAnchor="middle"
              dy={ChartAxisUtils.xTickDy}
            />
          )
        }}
      />
      {x.label ? (
        <text
          x={innerWidth / 2}
          y={
            innerHeight +
            tickLength +
            ChartAxisUtils.xLabelOffset() +
            ChartAxisStyles.tickLabel.fontSize +
            ChartAxisStyles.axisLabel.fontSize
          }
          fill={axisLabelFill}
          fontFamily={ChartAxisStyles.axisLabel.fontFamily}
          fontSize={ChartAxisStyles.axisLabel.fontSize}
          fontWeight={ChartAxisStyles.axisLabel.fontWeight}
          textAnchor="middle"
        >
          {x.label}
        </text>
      ) : null}
    </>
  )
}

const YAxis = ({ renderConfig }: ChartYAxisProps) => {
  const { padding, scales, innerHeight } = useChartWrapperContext()
  const { y, xAxisIsDate, x } = renderConfig.axis
  const axisStroke = ChartAxisUtils.useAxisStroke()
  const tickFill = ChartAxisUtils.useTickLabelFill()
  const axisLabelFill = ChartAxisUtils.useAxisLabelFill()

  if (scales == null) return null

  const isHorizontal = scales.orientation === 'horizontal'
  const tickLength = y.showTicks ? ChartAxisConstants.axisTickLength : 0
  const labelOffset = ChartAxisUtils.yLabelOffset(padding.left)
  const tickKind = isHorizontal
    ? xAxisIsDate
      ? ('date' as const)
      : ('text' as const)
    : ('number' as const)
  const tickOptions = {
    kind: tickKind,
    formatState: x,
    layout: y.axisTickLabelLayout,
    truncation: y.axisTickLabelTruncation,
  }

  return (
    <>
      <AxisLeft
        scale={scales.yScale}
        stroke={y.showLine ? axisStroke : 'transparent'}
        strokeWidth={ChartAxisStyles.axisStrokeWidth}
        strokeDasharray=""
        tickLength={tickLength}
        tickStroke={y.showTicks ? axisStroke : 'transparent'}
        tickValues={isHorizontal ? undefined : y.ticks}
        tickFormat={value =>
          ChartAxisUtils.resolveTickLabelLines({
            ...tickOptions,
            raw: String(value),
          }).join('\n')
        }
        tickComponent={({ x: tickX, y: tickY, formattedValue }) => {
          const bandwidth =
            isHorizontal &&
            'bandwidth' in scales.yScale &&
            typeof scales.yScale.bandwidth === 'function'
              ? scales.yScale.bandwidth()
              : 0
          const lines = String(formattedValue ?? '')
            .split('\n')
            .filter(Boolean)
          return (
            <TickLabel
              x={tickX}
              y={tickY + bandwidth / 2}
              lines={lines}
              orientation={renderConfig.axis.yTickLabelOrientation}
              fill={tickFill}
              textAnchor="end"
              dominantBaseline="middle"
              dx={ChartAxisUtils.yTickDx}
            />
          )
        }}
      />
      {y.label ? (
        <text
          fill={axisLabelFill}
          fontFamily={ChartAxisStyles.axisLabel.fontFamily}
          fontSize={ChartAxisStyles.axisLabel.fontSize}
          fontWeight={ChartAxisStyles.axisLabel.fontWeight}
          textAnchor="middle"
          dominantBaseline="middle"
          transform={`translate(${-(tickLength + labelOffset)}, ${innerHeight / 2}) rotate(-90)`}
        >
          {y.label}
        </text>
      ) : null}
    </>
  )
}

export const ChartAxis = {
  options: chartAxisOptions,
  setup: chartAxisSetup,
  XAxis,
  YAxis,
}
